/**
 * Entrar sin instalar nada, en desarrollo.
 *
 * Registering a device is the one operation that genuinely needs the network: the server hands
 * out a two-letter code that prefixes every ticket, which is what stops two offline terminals
 * both numbering a receipt A1-000042. That is load-bearing in a shop and pure ceremony on a
 * laptop, and it had a sharp edge — a device that cleared its own storage could not get back
 * in, because `/auth/bootstrap` correctly refuses an installed system and `/auth/dispositivo`
 * correctly wants the owner's PIN, which the wiped device no longer knew.
 *
 * So on a development server the app enrols itself, signs in as the owner, and fills an empty
 * shop with test data. `pnpm dev`, open the page, and you are looking at a working pulpería —
 * no screens, no token to copy, no PIN to invent.
 *
 * The gate is the server's: `/api/dev` is mounted only when it is not production and has no
 * SETUP_TOKEN, so on a real deployment these calls 404 and the install screen appears exactly
 * as it always did.
 */

import type { Centavos } from '@abastece/shared';
import { db, setMeta } from '../db/dexie.js';
import { api } from '../sync/client.js';
import { sincronizar } from '../sync/engine.js';
import { cargarDemo } from './demo.js';
import { abrirCaja, fondoQueQuedo, sesionAbierta } from './caja.js';
import { esCopiaDeTienda } from './copia-de-tienda.js';

/** Set when somebody deliberately asked for an empty shop, so auto-seeding leaves them alone. */
const CLAVE_VACIO = 'abastece.dev.vacio';

export const quiereVacio = (): boolean => {
  try {
    return localStorage.getItem(CLAVE_VACIO) === '1';
  } catch {
    return false;
  }
};

export const pedirVacio = (v: boolean): void => {
  try {
    if (v) localStorage.setItem(CLAVE_VACIO, '1');
    else localStorage.removeItem(CLAVE_VACIO);
  } catch {
    /* Without storage the next boot seeds again, which is the friendlier of the two mistakes. */
  }
};

/**
 * In-flight enrolment, shared by every caller.
 *
 * `StrictMode` invokes a mount effect twice, so `arrancar` called this twice on the same tick.
 * Both saw no token, both enrolled, and the second one's id overwrote the first — leaving the
 * shop with a device that had already emitted events under an id nobody remembered. The
 * visible symptom was the till: `caja.abierta` went out as device one, `meta.dispositivoId`
 * ended up as device two, and the home screen asked to open a till that was already open.
 *
 * A module-level promise makes the second call await the first instead of racing it, which is
 * also the right answer for the two-tabs case that has nothing to do with StrictMode.
 */
let enCurso: Promise<boolean> | null = null;

export function accesoDeDesarrollo(): Promise<boolean> {
  enCurso ??= enrolar().finally(() => {
    enCurso = null;
  });
  return enCurso;
}

/**
 * Enrol, sync, and seed if there is nothing to look at.
 *
 * Returns false when this is not a development server, which is the signal to fall back to
 * the ordinary install screen.
 */
async function enrolar(): Promise<boolean> {
  try {
    const salud = await api.health();
    if (!salud.instalacionAbierta) return false;

    const acceso = await api.devAcceso();
    await setMeta('dispositivoId', acceso.dispositivoId);
    await setMeta('dispositivoCodigo', acceso.codigo);
    await setMeta('token', acceso.token);
    await setMeta('usuarioActivoId', acceso.usuarioId);
    // The server consumed seq 1 registering this device, and 2 as well if it also had to
    // create the owner. Starting anywhere lower would collide with its own enrolment events.
    await setMeta('proximoDeviceSeq', acceso.creado ? 3 : 2);
    await setMeta('ultimoServerSeq', 0);

    // Pull first: the shop may already have everything, in which case seeding would be wrong.
    await sincronizar();

    // A copy of the real shop gets neither: the demo would put invented products beside the real
    // ones, and a test float would move cash in books that are the shop's (`copia-de-tienda.ts`).
    if (!quiereVacio() && !esCopiaDeTienda()) {
      if ((await db.productos.count()) === 0) await cargarDemo();
      else await abrirCajaSiHaceFalta();
    }
    return true;
  } catch {
    // No server, or a server that has no such door. Either way, the normal path still works.
    return false;
  }
}

/**
 * Back to nothing: the server's log and every projection, then this device's own copy.
 *
 * Server first. Wiping locally and then failing to reach the server would leave a device with
 * no credentials facing a system that still thinks it is installed — which is the exact dead
 * end this whole module exists to remove.
 */
export async function reiniciarTodo(conDatos: boolean): Promise<void> {
  await api.devReiniciar();
  pedirVacio(!conDatos);
  db.close();
  await db.delete();
  try {
    // Everything except the choice just made, which has to survive the reload to be obeyed.
    const vacio = localStorage.getItem(CLAVE_VACIO);
    localStorage.clear();
    if (vacio) localStorage.setItem(CLAVE_VACIO, vacio);
  } catch {
    /* The database is gone either way; the flags are conveniences. */
  }
  location.reload();
}

/**
 * A till for a device that arrived after the shop was already stocked.
 *
 * Sessions are per device on purpose — two terminals have two drawers, and a corte that mixed
 * them would be meaningless. But that means a freshly enrolled laptop never inherits the one
 * the seed opened, and lands on "La caja está cerrada" with no way to know why. On a
 * development server the honest answer is to give it its own drawer and get out of the way.
 */
async function abrirCajaSiHaceFalta(): Promise<void> {
  if (await sesionAbierta()) return;
  await abrirCaja(FONDO_DE_PRUEBA, 'tienda', 0 as Centavos, { relevo: await fondoQueQuedo('tienda') });
}

/** The same float the seed uses, so a reseeded shop and a re-enrolled one look alike. */
const FONDO_DE_PRUEBA = 200_000 as Centavos;
